/**
 * State machine for the Ask Melio surface.
 *
 * Deliberately holds one invariant: THE COMPOSER TEXT IS NEVER CLEARED UNTIL
 * THE SERVER HAS ACKNOWLEDGED THE MESSAGE. A member who loses connectivity
 * mid-send gets their words back and a retry button, rather than an empty box
 * and the feedback gone. That is the client half of the same
 * "feedback is never silently lost" property the server enforces by writing
 * before it calls anything.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { FEEDBACK_CHAT_ENABLED } from '../config/featureFlags';
import * as chatService from '../services/feedbackChatService';
import type {
  FeedbackChatBootstrap,
  FeedbackConversationSummary,
  FeedbackMessage,
  FeedbackRating,
  FeedbackSendState,
} from '../types/feedbackChat';

interface UseFeedbackChat {
  loading: boolean;
  available: boolean;
  bootstrap: FeedbackChatBootstrap | null;
  acknowledged: boolean;
  acknowledge: () => Promise<void>;

  conversationId: string | null;
  messages: FeedbackMessage[];
  conversations: FeedbackConversationSummary[];

  draft: string;
  setDraft: (value: string) => void;
  sendState: FeedbackSendState;
  errorMessage: string | null;

  send: (text?: string) => Promise<void>;
  retry: () => Promise<void>;
  startNewConversation: () => Promise<void>;
  openConversation: (id: string) => void;
  rate: (messageId: string, rating: FeedbackRating) => Promise<void>;
  escalate: (note?: string) => Promise<void>;

  atMessageLimit: boolean;
  maxChars: number;
}

export function useFeedbackChat(openedFromPath?: string): UseFeedbackChat {
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [bootstrap, setBootstrap] = useState<FeedbackChatBootstrap | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<FeedbackMessage[]>([]);
  const [conversations, setConversations] = useState<FeedbackConversationSummary[]>([]);
  const [draft, setDraft] = useState('');
  const [sendState, setSendState] = useState<FeedbackSendState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Held so a failed send can be retried with the same idempotency key — the
  // server dedupes on it, so a retry after a timeout cannot double-post.
  const pendingRef = useRef<{ text: string; clientMessageId: string } | null>(null);

  const available = FEEDBACK_CHAT_ENABLED && Boolean(bootstrap?.enabled);
  const acknowledged = Boolean(bootstrap?.acknowledged);
  const maxChars = bootstrap?.limits?.maxMessageChars ?? 2000;

  const atMessageLimit = useMemo(() => {
    const max = bootstrap?.limits?.maxMessagesPerConversation ?? 30;
    return messages.length >= max;
  }, [messages.length, bootstrap]);

  // ------------------------------------------------------------- bootstrap

  useEffect(() => {
    let cancelled = false;

    if (!FEEDBACK_CHAT_ENABLED || !currentUser) {
      setLoading(false);
      return undefined;
    }

    chatService.fetchBootstrap()
      .then((data) => {
        if (!cancelled) setBootstrap(data);
      })
      .catch((error) => {
        // The member sees the same thing either way — the surface simply is not
        // there — but an operator must be able to tell "switched off" from
        // "broken", and silently collapsing the two made a missing deployment
        // look identical to a disabled feature.
        //
        // 'not-found' is the one worth calling out: it means the callable is
        // not deployed at all, which is the likeliest cause the first time this
        // is stood up in an environment.
        const code = error?.code || 'unknown';
        // eslint-disable-next-line no-console
        console.error(
          code === 'functions/not-found' || code === 'not-found'
            ? '[Ask Melio] feedbackChatBootstrap is not deployed in this project. '
              + 'Deploy functions (npm run deploy:staging:functions) — the client flag alone is not enough.'
            : `[Ask Melio] bootstrap failed (${code}). The surface will not render.`,
          error,
        );
        if (!cancelled) setBootstrap({ enabled: false });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentUser]);

  // ------------------------------------------------------ conversation list

  const refreshConversations = useCallback(async () => {
    if (!currentUser) return;
    try {
      setConversations(await chatService.fetchConversations(currentUser.uid));
    } catch {
      // The history list is a convenience; failing to load it must not break
      // the ability to send.
    }
  }, [currentUser]);

  useEffect(() => {
    if (available && acknowledged) void refreshConversations();
  }, [available, acknowledged, refreshConversations]);

  // ---------------------------------------------------------- live messages

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return undefined;
    }
    return chatService.subscribeToMessages(
      conversationId,
      setMessages,
      () => setErrorMessage('We could not load this conversation. Please try again.'),
    );
  }, [conversationId]);

  // --------------------------------------------------------------- actions

  const acknowledge = useCallback(async () => {
    if (!bootstrap?.disclaimerVersion) return;
    await chatService.acknowledgeDisclaimer(bootstrap.disclaimerVersion);
    setBootstrap((prev) => (prev ? { ...prev, acknowledged: true } : prev));
  }, [bootstrap]);

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationId) return conversationId;
    const id = await chatService.startConversation(openedFromPath);
    setConversationId(id);
    return id;
  }, [conversationId, openedFromPath]);

  const doSend = useCallback(async (text: string, clientMessageId: string) => {
    setSendState('sending');
    setErrorMessage(null);

    try {
      const id = await ensureConversation();
      await chatService.sendMessage({
        conversationId: id,
        text,
        clientMessageId,
        openedFromPath,
      });

      // Only now is it safe to clear the box.
      pendingRef.current = null;
      setDraft('');
      setSendState('idle');
      void refreshConversations();
    } catch (error: any) {
      pendingRef.current = { text, clientMessageId };
      setSendState('error');
      setErrorMessage(
        error?.message
        || 'That message could not be sent. Your text has been kept — please try again.',
      );
    }
  }, [ensureConversation, openedFromPath, refreshConversations]);

  const send = useCallback(async (text?: string) => {
    const body = (text ?? draft).trim();
    if (!body) return;
    await doSend(body, chatService.newClientMessageId());
  }, [draft, doSend]);

  const retry = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) return;
    // Same idempotency key, so a send that actually succeeded before the
    // connection dropped resolves to the existing message instead of a duplicate.
    await doSend(pending.text, pending.clientMessageId);
  }, [doSend]);

  const startNewConversation = useCallback(async () => {
    setConversationId(null);
    setMessages([]);
    setDraft('');
    setErrorMessage(null);
    setSendState('idle');
    pendingRef.current = null;
  }, []);

  const openConversation = useCallback((id: string) => {
    setConversationId(id);
    setErrorMessage(null);
  }, []);

  const rate = useCallback(async (messageId: string, rating: FeedbackRating) => {
    if (!conversationId) return;
    try {
      await chatService.rateMessage(conversationId, messageId, rating);
    } catch {
      // A rating is best-effort; never surface a failure for it.
    }
  }, [conversationId]);

  const escalate = useCallback(async (note?: string) => {
    if (!conversationId) return;
    await chatService.escalateConversation(conversationId, note);
    void refreshConversations();
  }, [conversationId, refreshConversations]);

  return {
    loading,
    available,
    bootstrap,
    acknowledged,
    acknowledge,
    conversationId,
    messages,
    conversations,
    draft,
    setDraft,
    sendState,
    errorMessage,
    send,
    retry,
    startNewConversation,
    openConversation,
    rate,
    escalate,
    atMessageLimit,
    maxChars,
  };
}
